import React, { useState, useEffect } from 'react';
import { Lock, CheckCircle, ChevronDown, ChevronRight } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

// Category metadata
const CATEGORY_META = {
  milestone: { label: 'Milestones', color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-950/30', border: 'border-blue-200 dark:border-blue-800', icon: '🏆' },
  streak:    { label: 'Streaks',    color: 'text-orange-600 dark:text-orange-400', bg: 'bg-orange-50 dark:bg-orange-950/30', border: 'border-orange-200 dark:border-orange-800', icon: '🔥' },
  learning:  { label: 'Learning',   color: 'text-green-600 dark:text-green-400', bg: 'bg-green-50 dark:bg-green-950/30', border: 'border-green-200 dark:border-green-800', icon: '📚' },
  engagement:{ label: 'Engagement', color: 'text-purple-600 dark:text-purple-400', bg: 'bg-purple-50 dark:bg-purple-950/30', border: 'border-purple-200 dark:border-purple-800', icon: '✨' },
  sabbath:   { label: 'Sabbath',    color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-950/30', border: 'border-amber-200 dark:border-amber-800', icon: '🕯️' },
};

// For each trigger, what user stat drives progress
const TRIGGER_STAT_LABELS = {
  VERSE_ADDED:           'verses added',
  VERSE_MILESTONE:       'verses in library',
  SESSION_COMPLETED:     'sessions completed',
  SESSION_MILESTONE:     'sessions completed',
  REFLECTION_WRITTEN:    'reflections written',
  REFLECTION_MILESTONE:  'reflections written',
  STREAK_MILESTONE:      'day streak',
  STREAK_FREEZE_USED:    'streak freezes used',
  WEEKLY_VERSE_STREAK:   'consecutive weeks',
  WEEKLY_VERSE_STREAK_2X:'consecutive weeks (2+ verses)',
  VERSE_MASTERED:        'verses mastered (Phase 7)',
  BOOK_VARIETY:          'unique books',
  READING_PLAN_COMPLETED:'reading plans completed',
  TAG_CREATED:           'tags created',
  TAG_MILESTONE:         'tags created',
  EARLY_RISER:           'early sessions (before 8am)',
  NIGHT_AND_DAY:         'night+day sessions',
  SABBATH_VERSE_MILESTONE:'sabbath verses',
};

const AwardsProgressPanel = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [definitions, setDefinitions] = useState([]);
  const [userAwards, setUserAwards] = useState({});
  const [userStats, setUserStats] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({ milestone: true, streak: true, learning: true, engagement: true, sabbath: true });
  const [filterMode, setFilterMode] = useState('all'); // all | earned | unearned

  useEffect(() => {
    if (!user?.id) return;
    loadData();
  }, [user?.id]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load all award definitions
      const { data: defs } = await supabase
        ?.from('award_definitions')
        ?.select('*')
        ?.order('sort_order', { ascending: true });

      // Load user's earned awards
      const { data: earned } = await supabase
        ?.from('user_awards')
        ?.select('award_slug, times_earned, earned_at')
        ?.eq('user_id', user?.id);

      const earnedMap = {};
      (earned || [])?.forEach(a => { earnedMap[a?.award_slug] = { timesEarned: a?.times_earned, earnedAt: a?.earned_at }; });

      // Load user stats needed for progress calculation
      const stats = await fetchUserStats();

      setDefinitions(defs || []);
      setUserAwards(earnedMap);
      setUserStats(stats);
    } catch (err) {
      console.error('AwardsProgressPanel load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserStats = async () => {
    const stats = {};
    try {
      // Total verses
      const { data: verses } = await supabase
        ?.from('verses')
        ?.select('current_phase, book, created_at')
        ?.eq('user_id', user?.id);

      stats.totalVerses = verses?.length || 0;
      stats.uniqueBooks = new Set(verses?.map(v => v?.book))?.size || 0;
      stats.masteredVerses = verses?.filter(v => v?.current_phase >= 7)?.length || 0;

      // Total sessions
      const { count: sessionCount } = await supabase
        ?.from('user_review_sessions')
        ?.select('id', { count: 'exact', head: true })
        ?.eq('user_id', user?.id)
        ?.eq('is_complete', true);
      stats.totalSessions = sessionCount || 0;

      // Reflections
      const { count: reflectionCount } = await supabase
        ?.from('verse_reflections')
        ?.select('id', { count: 'exact', head: true })
        ?.eq('user_id', user?.id);
      stats.totalReflections = reflectionCount || 0;

      // Tags
      const { count: tagCount } = await supabase
        ?.from('verse_tags')
        ?.select('id', { count: 'exact', head: true })
        ?.eq('user_id', user?.id);
      stats.totalTags = tagCount || 0;

      // Streak
      const { data: streakData } = await supabase
        ?.from('user_streaks')
        ?.select('current_streak, longest_streak')
        ?.eq('user_id', user?.id)
        ?.maybeSingle();
      stats.currentStreak = streakData?.current_streak || 0;
      stats.longestStreak = streakData?.longest_streak || 0;

      // Reading plans completed
      const { count: planCount } = await supabase
        ?.from('user_reading_plans')
        ?.select('id', { count: 'exact', head: true })
        ?.eq('user_id', user?.id)
        ?.eq('status', 'completed');
      stats.totalPlans = planCount || 0;

      // Early riser sessions
      const { count: earlyCount } = await supabase
        ?.from('user_review_sessions')
        ?.select('id', { count: 'exact', head: true })
        ?.eq('user_id', user?.id)
        ?.lt('start_hour', 8)
        ?.eq('is_complete', true);
      stats.earlySessions = earlyCount || 0;

      // Weekly verse streak — count consecutive weeks with Phase 1+ verse adds
      const phase1Verses = verses?.filter(v => v?.current_phase >= 1) || [];
      stats.weeklyVerseStreak = computeWeeklyStreak(phase1Verses, 1);
      stats.weeklyVerseStreak2x = computeWeeklyStreak(phase1Verses, 2);

    } catch (err) {
      console.error('fetchUserStats error:', err);
    }
    return stats;
  };

  const computeWeeklyStreak = (verses, minPerWeek) => {
    if (!verses?.length) return 0;
    const getISOWeekKey = (dateStr) => {
      const d = new Date(dateStr);
      const day = d?.getUTCDay();
      const diff = day === 0 ? -6 : 1 - day;
      const monday = new Date(d);
      monday?.setUTCDate(d?.getUTCDate() + diff);
      return `${monday?.getUTCFullYear()}-${String(monday?.getUTCMonth() + 1)?.padStart(2, '0')}-${String(monday?.getUTCDate())?.padStart(2, '0')}`;
    };
    const weekMap = {};
    verses?.forEach(v => {
      const key = getISOWeekKey(v?.created_at);
      weekMap[key] = (weekMap?.[key] || 0) + 1;
    });
    const today = new Date();
    let consecutive = 0;
    let checkDate = new Date(today);
    for (let i = 0; i < 52; i++) {
      const key = getISOWeekKey(checkDate?.toISOString());
      if ((weekMap?.[key] || 0) >= minPerWeek) {
        consecutive++;
        checkDate?.setUTCDate(checkDate?.getUTCDate() - 7);
      } else break;
    }
    return consecutive;
  };

  // Get current progress value for a given award definition
  const getProgressValue = (def) => {
    switch (def?.trigger_event) {
      case 'VERSE_ADDED': case'VERSE_MILESTONE':
        return userStats?.totalVerses || 0;
      case 'SESSION_COMPLETED': case'SESSION_MILESTONE':
        if (def?.slug === 'scripture_scholar' || def?.slug === 'perfect_practice') return null; // accuracy-based
        return userStats?.totalSessions || 0;
      case 'REFLECTION_WRITTEN': case'REFLECTION_MILESTONE':
        return userStats?.totalReflections || 0;
      case 'STREAK_MILESTONE':
        return userStats?.currentStreak || 0;
      case 'STREAK_FREEZE_USED':
        return userAwards?.['streak_freeze']?.timesEarned || 0;
      case 'WEEKLY_VERSE_STREAK':
        return userStats?.weeklyVerseStreak || 0;
      case 'WEEKLY_VERSE_STREAK_2X':
        return userStats?.weeklyVerseStreak2x || 0;
      case 'VERSE_MASTERED':
        return userStats?.masteredVerses || 0;
      case 'BOOK_VARIETY':
        return userStats?.uniqueBooks || 0;
      case 'READING_PLAN_COMPLETED':
        return userStats?.totalPlans || 0;
      case 'TAG_CREATED': case'TAG_MILESTONE':
        return userStats?.totalTags || 0;
      case 'EARLY_RISER':
        return userStats?.earlySessions || 0;
      case 'NIGHT_AND_DAY':
        return userAwards?.['night_watch']?.timesEarned || 0;
      case 'SABBATH_VERSE_MILESTONE':
        return 0; // not yet implemented
      default:
        return 0;
    }
  };

  // Compute progress bar percentage and countdown text
  const getAwardProgress = (def) => {
    const earned = userAwards?.[def?.slug];
    const timesEarned = earned?.timesEarned || 0;
    const currentValue = getProgressValue(def);

    // Accuracy-based awards — special case
    if (def?.slug === 'scripture_scholar' || def?.slug === 'perfect_practice') {
      const isEarned = timesEarned >= 1;
      return {
        isEarned,
        percent: isEarned ? 100 : 0,
        currentValue: null,
        threshold: null,
        countdownText: isEarned ? 'Earned!' : 'Complete a practice session with required accuracy',
        isAccuracyBased: true,
      };
    }

    if (!def?.threshold) {
      // No threshold — binary (earned or not)
      const isEarned = timesEarned >= 1;
      return {
        isEarned,
        percent: isEarned ? 100 : 0,
        currentValue,
        threshold: null,
        countdownText: isEarned ? 'Earned!' : 'Complete the required action to unlock',
      };
    }

    if (def?.repeatable) {
      // Repeatable: show progress toward next multiple
      const nextTarget = (timesEarned + 1) * def?.threshold;
      const prevTarget = timesEarned * def?.threshold;
      const progressInWindow = Math.max(0, (currentValue || 0) - prevTarget);
      const windowSize = def?.threshold;
      const percent = Math.min(100, Math.round((progressInWindow / windowSize) * 100));
      const remaining = Math.max(0, nextTarget - (currentValue || 0));
      return {
        isEarned: timesEarned >= 1,
        percent,
        currentValue,
        threshold: nextTarget,
        timesEarned,
        countdownText: remaining === 0
          ? `Next unlock ready!`
          : `${remaining} more ${TRIGGER_STAT_LABELS?.[def?.trigger_event] || 'actions'} to next unlock`,
      };
    } else {
      // Non-repeatable: progress toward single threshold
      const isEarned = timesEarned >= 1;
      const percent = isEarned ? 100 : Math.min(99, Math.round(((currentValue || 0) / def?.threshold) * 100));
      const remaining = Math.max(0, def?.threshold - (currentValue || 0));
      return {
        isEarned,
        percent,
        currentValue,
        threshold: def?.threshold,
        countdownText: isEarned
          ? 'Earned!'
          : `${remaining} more ${TRIGGER_STAT_LABELS?.[def?.trigger_event] || 'actions'} to unlock`,
      };
    }
  };

  // Group definitions by category
  const grouped = {};
  definitions?.forEach(def => {
    const cat = def?.category || 'milestone';
    if (!grouped?.[cat]) grouped[cat] = [];
    grouped?.[cat]?.push(def);
  });

  const toggleCategory = (cat) => {
    setExpandedCategories(prev => ({ ...prev, [cat]: !prev?.[cat] }));
  };

  // Summary stats
  const totalAwards = definitions?.length || 0;
  const earnedCount = Object.keys(userAwards)?.length || 0;
  const totalGold = definitions?.reduce((sum, d) => {
    const earned = userAwards?.[d?.slug];
    return sum + (earned ? (d?.gold_reward || 0) * (earned?.timesEarned || 1) : 0);
  }, 0);
  const totalSilver = definitions?.reduce((sum, d) => {
    const earned = userAwards?.[d?.slug];
    return sum + (earned ? (d?.silver_reward || 0) * (earned?.timesEarned || 1) : 0);
  }, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Banner */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-card rounded-lg border border-border p-4 text-center">
          <p className="text-2xl font-bold text-foreground">{earnedCount}</p>
          <p className="text-xs text-muted-foreground mt-1">Awards Earned</p>
        </div>
        <div className="bg-card rounded-lg border border-border p-4 text-center">
          <p className="text-2xl font-bold text-foreground">{totalAwards}</p>
          <p className="text-xs text-muted-foreground mt-1">Total Awards</p>
        </div>
        <div className="bg-card rounded-lg border border-border p-4 text-center">
          <p className="text-2xl font-bold text-yellow-500">🥇 {totalGold}</p>
          <p className="text-xs text-muted-foreground mt-1">Gold Earned</p>
        </div>
        <div className="bg-card rounded-lg border border-border p-4 text-center">
          <p className="text-2xl font-bold text-gray-400">🥈 {totalSilver}</p>
          <p className="text-xs text-muted-foreground mt-1">Silver Earned</p>
        </div>
      </div>

      {/* Overall progress bar */}
      <div className="bg-card rounded-lg border border-border p-5">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-foreground">Overall Award Progress</span>
          <span className="text-sm font-semibold text-primary">{earnedCount} / {totalAwards}</span>
        </div>
        <div className="w-full bg-muted rounded-full h-3 overflow-hidden">
          <div
            className="h-3 rounded-full bg-gradient-to-r from-primary to-success transition-all duration-700"
            style={{ width: `${totalAwards > 0 ? Math.round((earnedCount / totalAwards) * 100) : 0}%` }}
          />
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          {totalAwards > 0 ? Math.round((earnedCount / totalAwards) * 100) : 0}% complete
        </p>
      </div>

      {/* Filter Tabs */}
      <div className="flex items-center gap-2">
        {['all', 'earned', 'unearned']?.map(mode => (
          <button
            key={mode}
            onClick={() => setFilterMode(mode)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors capitalize ${
              filterMode === mode
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            {mode}
          </button>
        ))}
      </div>

      {/* Award Categories */}
      {Object.entries(CATEGORY_META)?.map(([catKey, catMeta]) => {
        const awards = grouped?.[catKey] || [];
        if (!awards?.length) return null;

        const filteredAwards = awards?.filter(def => {
          const prog = getAwardProgress(def);
          if (filterMode === 'earned') return prog?.isEarned;
          if (filterMode === 'unearned') return !prog?.isEarned;
          return true;
        });

        if (!filteredAwards?.length) return null;

        const catEarned = awards?.filter(d => userAwards?.[d?.slug])?.length;
        const isExpanded = expandedCategories?.[catKey];

        return (
          <div key={catKey} className={`rounded-lg border overflow-hidden ${catMeta?.border}`}>
            {/* Category Header */}
            <button
              onClick={() => toggleCategory(catKey)}
              className={`w-full flex items-center justify-between px-4 py-3 ${catMeta?.bg} hover:opacity-90 transition-opacity`}
            >
              <div className="flex items-center gap-3">
                {isExpanded
                  ? <ChevronDown className={`w-4 h-4 ${catMeta?.color}`} />
                  : <ChevronRight className={`w-4 h-4 ${catMeta?.color}`} />
                }
                <span className="text-lg">{catMeta?.icon}</span>
                <span className={`font-semibold text-sm ${catMeta?.color}`}>{catMeta?.label}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-muted-foreground">{catEarned}/{awards?.length} earned</span>
                <div className="w-20 bg-muted rounded-full h-1.5 hidden sm:block">
                  <div
                    className="h-1.5 rounded-full bg-current transition-all"
                    style={{ width: `${awards?.length > 0 ? Math.round((catEarned / awards?.length) * 100) : 0}%`, color: 'var(--color-primary)' }}
                  />
                </div>
              </div>
            </button>

            {/* Award Cards */}
            {isExpanded && (
              <div className="divide-y divide-border">
                {filteredAwards?.map(def => {
                  const prog = getAwardProgress(def);
                  const earned = userAwards?.[def?.slug];

                  return (
                    <div
                      key={def?.slug}
                      className={`px-4 py-4 flex flex-col sm:flex-row sm:items-center gap-3 bg-card transition-colors ${
                        prog?.isEarned ? 'opacity-100' : 'opacity-80'
                      }`}
                    >
                      {/* Icon + Name */}
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-xl flex-shrink-0 ${
                          prog?.isEarned ? catMeta?.bg : 'bg-muted'
                        }`}>
                          {prog?.isEarned
                            ? <span>{def?.icon}</span>
                            : <Lock className="w-4 h-4 text-muted-foreground" />
                          }
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-sm text-foreground">{def?.name}</span>
                            {prog?.isEarned && (
                              <span className="flex items-center gap-1 text-xs text-success font-medium">
                                <CheckCircle className="w-3 h-3" /> Earned
                              </span>
                            )}
                            {def?.repeatable && prog?.timesEarned > 0 && (
                              <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded-full font-medium">
                                ×{prog?.timesEarned}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{def?.description}</p>

                          {/* Rewards */}
                          <div className="flex items-center gap-3 mt-1.5">
                            {(def?.gold_reward || 0) > 0 && (
                              <span className="text-xs text-yellow-600 dark:text-yellow-400 font-medium">🥇 {def?.gold_reward} gold</span>
                            )}
                            {(def?.silver_reward || 0) > 0 && (
                              <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">🥈 {def?.silver_reward} silver</span>
                            )}
                            {def?.repeatable && (
                              <span className="text-xs text-muted-foreground italic">Repeatable</span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Progress Section */}
                      <div className="sm:w-56 flex-shrink-0">
                        {prog?.isAccuracyBased ? (
                          <div className="text-xs text-muted-foreground italic">{prog?.countdownText}</div>
                        ) : (
                          <>
                            {/* Progress bar */}
                            <div className="w-full bg-muted rounded-full h-2 mb-1.5">
                              <div
                                className={`h-2 rounded-full transition-all duration-500 ${
                                  prog?.isEarned ? 'bg-success' : 'bg-primary'
                                }`}
                                style={{ width: `${prog?.percent}%` }}
                              />
                            </div>
                            {/* Progress numbers */}
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-muted-foreground">
                                {prog?.currentValue !== null && prog?.currentValue !== undefined
                                  ? `${prog?.currentValue}${prog?.threshold ? ` / ${prog?.threshold}` : ''}`
                                  : '—'
                                }
                              </span>
                              <span className={`font-medium ${prog?.isEarned ? 'text-success' : 'text-primary'}`}>
                                {prog?.percent}%
                              </span>
                            </div>
                            {/* Countdown text */}
                            <p className={`text-xs mt-1 ${prog?.isEarned ? 'text-success' : 'text-muted-foreground'}`}>
                              {prog?.countdownText}
                            </p>
                            {/* Earned date */}
                            {prog?.isEarned && earned?.earnedAt && (
                              <p className="text-xs text-muted-foreground/60 mt-0.5">
                                Earned {new Date(earned?.earnedAt)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                              </p>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default AwardsProgressPanel;
